import React from 'react';

import AutoEnrolIcon from 'icons/double-arrow-rotate.svg';
import pluralize from 'pluralize';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import PersonalInfo from 'components/application/PersonalInfo';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';

import {
  currencyNumberToCurrencyString,
  displayCurrencyString,
} from 'components/helpers/currency';
import { formatDuration } from 'components/helpers/dates';
import { isCourseEligibleForAutoEnrolment } from 'components/helpers/resources/courses';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function BookingCourseDetails({ course }) {
  const currentActor = useCurrentActor();

  const { isAutoEnrolmentIncludingMissingTraining } =
    useTrainingRegisterResources();

  const { t: i18nTCommon } = useTranslation('common');

  const {
    costEstimate,
    expires,
    learningMethod,
    provider,
    renewalFrequency,
    renewalInterval,
    timeAmount,
    timeInterval,
  } = course.attributes;

  const { currencyCode } = currentActor.division.attributes;

  const courseCostEstimate = currencyNumberToCurrencyString({
    number: costEstimate,
    currencyCode,
  });

  return (
    <>
      <PersonalInfo
        label={i18nTCommon('courses.renewalFrequency')}
        text={
          expires ?
            voca.isBlank(renewalFrequency) ?
              ''
            : formatDuration({
                durationParts: {
                  [pluralize(renewalInterval)]: renewalFrequency,
                },
              })

          : i18nTCommon('shared.doesNotExpire')
        }
      />
      <div className='personal-info'>
        <div className='personal-info__info-container'>
          <div className='personal-info__label tw-font-medium tw-text-grey-900'>
            {i18nTCommon('shared.method')}
          </div>
          <div className='personal-info__text'>
            {(
              isCourseEligibleForAutoEnrolment({
                currentActor,
                isAutoEnrolmentIncludingMissingTraining,
                course,
              })
            ) ?
              <div className='tw-flex tw-items-center tw-justify-between'>
                <span>
                  {i18nTCommon('courses.learningMethods.eLearning')} (
                </span>
                <AutoEnrolIcon className='[&_path]:tw-fill-grey-700' />
                <span>{i18nTCommon('courses.autoEnrols')})</span>
              </div>
            : learningMethod && (
                <span>
                  {i18nTCommon(
                    `courses.learningMethods.${voca.camelCase(learningMethod)}`,
                  )}
                </span>
              )
            }
          </div>
        </div>
      </div>
      <PersonalInfo label={i18nTCommon('courses.provider')} text={provider} />
      <PersonalInfo
        label={i18nTCommon('courses.costEstimate')}
        text={displayCurrencyString({
          string: courseCostEstimate,
          currencyCode,
        })}
      />
      <PersonalInfo
        label={i18nTCommon('courses.timeEstimate')}
        text={
          voca.isBlank(timeAmount) ? '' : (
            formatDuration({
              durationParts: { [pluralize(timeInterval)]: timeAmount },
            })
          )
        }
      />
    </>
  );
}

BookingCourseDetails.propTypes = {
  course: resourceShape('course').isRequired,
};
